import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function formatPoly(coeffs) {
  const parts = [];
  for (let degree = coeffs.length - 1; degree >= 0; degree -= 1) {
    const coeff = coeffs[degree];
    if (coeff === 0) continue;

    let term = "";
    if (degree === 0) {
      term = `${Math.abs(coeff)}`;
    } else if (degree === 1) {
      term = Math.abs(coeff) === 1 ? "x" : `${Math.abs(coeff)}x`;
    } else {
      term = Math.abs(coeff) === 1 ? `x^${degree}` : `${Math.abs(coeff)}x^${degree}`;
    }

    if (parts.length === 0) {
      parts.push(coeff < 0 ? `-${term}` : term);
    } else {
      parts.push(coeff < 0 ? `- ${term}` : `+ ${term}`);
    }
  }
  return parts.join(" ");
}

function lineLatex(m, d) {
  if (m === 0) return `${d}`;
  const mPart = m === 1 ? "x" : m === -1 ? "-x" : `${m}x`;
  if (d === 0) return mPart;
  return d > 0 ? `${mPart} + ${d}` : `${mPart} - ${Math.abs(d)}`;
}

function lineLatexExact(aLatex, bLatex, aValue, bValue) {
  if (aValue === 0) return `${bLatex}`;
  const aPart = aValue === 1 ? "x" : aValue === -1 ? "-x" : `${aLatex}x`;
  if (bValue === 0) return aPart;
  return bValue > 0 ? `${aPart} + ${bLatex}` : `${aPart} - ${bLatex.replace("-", "")}`;
}

function lineLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) return `y = ${bLatex}`;

  const aPart = aParsed === 1 ? "x" : aParsed === -1 ? "-x" : `${aLatex}x`;
  if (bParsed === 0) return `y = ${aPart}`;
  return `y = ${aPart} ${bParsed >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function numeratorValue(a, b, c, x) {
  return a * x * x + b * x + c;
}

function buildWindow(center, span = 10) {
  let start = Math.round(center) - span / 2;
  let end = start + span;
  if (start > 0) {
    end -= start;
    start = 0;
  } else if (end < 0) {
    start -= end;
    end = 0;
  }
  return [Math.round(start), Math.round(end)];
}

function buildRanges(x0, y0) {
  return {
    xRange: buildWindow(x0),
    yRange: buildWindow(y0),
  };
}

function weightedX0() {
  const outer = [-4, -3, 3, 4];
  const inner = [-2, -1, 0, 1, 2];
  return Math.random() < 0.75 ? choice(outer) : choice(inner);
}

function rationalValue(na, nb, nc, da, db, x) {
  return numeratorValue(na, nb, nc, x) / (da * x + db);
}

function simplifyFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);
  const simpNum = absNum / d;
  const simpDen = absDen / d;
  if (simpDen === 1) return `${sign * simpNum}`;
  return `${sign < 0 ? "-" : ""}\\frac{${simpNum}}{${simpDen}}`;
}

function reduceFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  if (denominator < 0) {
    numerator *= -1;
    denominator *= -1;
  }
  const d = gcd(Math.abs(numerator), Math.abs(denominator));
  return { numerator: numerator / d, denominator: denominator / d };
}

function addFractions(aNum, aDen, bNum, bDen) {
  return reduceFraction(aNum * bDen + bNum * aDen, aDen * bDen);
}

function subtractFractions(aNum, aDen, bNum, bDen) {
  return reduceFraction(aNum * bDen - bNum * aDen, aDen * bDen);
}

function generateCoefficients() {
  for (let attempt = 0; attempt < 1200; attempt += 1) {
    const x0 = weightedX0();
    const na = randInt(-2, 2, [0]);
    const nb = randInt(-4, 4);
    const nc = randInt(-5, 5);
    const da = randInt(-4, 4, [0]);
    const db = randInt(-5, 5, [0]);

    if (da * x0 + db === 0) continue;

    const y0raw = rationalValue(na, nb, nc, da, db, x0);
    if (!Number.isFinite(y0raw)) continue;
    if (Math.abs(y0raw) > 4) continue;

    const numDer = (2 * na * x0 + nb) * (da * x0 + db) - numeratorValue(na, nb, nc, x0) * da;
    const denDer = (da * x0 + db) ** 2;
    const m = numDer / denDer;
    if (!Number.isFinite(m) || Math.abs(m) > 5 || Math.abs(m) < 0.25) continue;

    const y0Fraction = reduceFraction(
      numeratorValue(na, nb, nc, x0),
      da * x0 + db
    );
    const mFraction = reduceFraction(numDer, denDer);
    const dFraction = subtractFractions(
      y0Fraction.numerator,
      y0Fraction.denominator,
      mFraction.numerator * x0,
      mFraction.denominator
    );
    const d = dFraction.numerator / dFraction.denominator;
    const { xRange, yRange } = buildRanges(x0, y0raw);
    const y0 = y0raw;

    const localVisible = [x0 - 1, x0, x0 + 1]
      .filter((x) => x >= xRange[0] && x <= xRange[1] && da * x + db !== 0)
      .every((x) => {
        const y = rationalValue(na, nb, nc, da, db, x);
        return Number.isFinite(y) && y >= yRange[0] && y <= yRange[1];
      });

    const fractionsReasonable = mFraction.denominator <= 50 && dFraction.denominator <= 50;

    if (fractionsReasonable && localVisible && d >= yRange[0] - 2 && d <= yRange[1] + 2) {
      return {
        na, nb, nc, da, db, x0, y0, m, d, xRange, yRange,
        y0Fraction,
        mFraction,
        dFraction,
      };
    }
  }

  const na = 1;
  const nb = -2;
  const nc = -3;
  const da = 1;
  const db = 2;
  const x0 = 2;
  const y0 = rationalValue(na, nb, nc, da, db, x0);
  const numDer = (2 * na * x0 + nb) * (da * x0 + db) - numeratorValue(na, nb, nc, x0) * da;
  const denDer = (da * x0 + db) ** 2;
  const m = numDer / denDer;
  const y0Fraction = reduceFraction(numeratorValue(na, nb, nc, x0), da * x0 + db);
  const mFraction = reduceFraction(numDer, denDer);
  const dFraction = subtractFractions(
    y0Fraction.numerator,
    y0Fraction.denominator,
    mFraction.numerator * x0,
    mFraction.denominator
  );
  const d = dFraction.numerator / dFraction.denominator;
  const { xRange, yRange } = buildRanges(x0, y0);
  return { na, nb, nc, da, db, x0, y0, m, d, xRange, yRange, y0Fraction, mFraction, dFraction };
}

function rationalLatex(na, nb, nc, da, db) {
  const numerator = formatPoly([nc, nb, na]);
  const denominator = formatPoly([db, da]);
  return `\\frac{${numerator}}{${denominator}}`;
}

function exprFromCoeffs(coeffs) {
  const parts = [];
  for (let degree = coeffs.length - 1; degree >= 0; degree -= 1) {
    const coeff = coeffs[degree];
    if (coeff === 0) continue;

    let term = "";
    if (degree === 0) {
      term = `${coeff}`;
    } else if (degree === 1) {
      term = `${coeff}x`;
    } else {
      term = `${coeff}x^${degree}`;
    }

    if (parts.length === 0) {
      parts.push(term);
    } else {
      parts.push(coeff > 0 ? `+${term}` : term);
    }
  }

  return parts.length ? parts.join("") : "0";
}

function rationalExpr(na, nb, nc, da, db) {
  const numerator = exprFromCoeffs([nc, nb, na]);
  const denominator = exprFromCoeffs([db, da]);
  return `y=(${numerator})/(${denominator})`;
}

function lineExpr(m, d) {
  if (m === 0) return `y=${d}`;
  return `y=${m}x${d > 0 ? `+${d}` : d < 0 ? `${d}` : ""}`;
}

export function generateTask() {
  const {
    na, nb, nc, da, db, x0, y0, m, d, xRange, yRange,
    y0Fraction, mFraction, dFraction,
  } = generateCoefficients();

  return {
    type: "RationalTangentTask",
    prompt: `Wyznacz rownanie stycznej do wykresu funkcji f(x) = ${rationalLatex(na, nb, nc, da, db)} w punkcie o odcietej x_0 = ${x0}. Podaj wspolczynniki a i b prostej y = ax + b.`,
    answer: `a=${m}, b=${d}`,
    data: {
      na,
      nb,
      nc,
      da,
      db,
      x0,
      y0,
      tangentA: m,
      tangentB: d,
      tangentAFraction: mFraction,
      tangentBFraction: dFraction,
      y0Fraction,
      functionLatex: rationalLatex(na, nb, nc, da, db),
      tangentLatex: lineLatex(m, d),
      xRange,
      yRange,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz rownanie stycznej do wykresu funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>w punkcie o odcietej <InlineMath math={`x_0 = ${data.x0}`} />.</p>
      <p>Podaj wspolczynniki <InlineMath math={"a"} /> i <InlineMath math={"b"} /> prostej <InlineMath math={"y=ax+b"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="a =" /></div>
        <FractionInput
          value={value.answerA || ""}
          onChange={(next) => onChange({ ...value, answerA: next })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="b =" /></div>
        <FractionInput
          value={value.answerB || ""}
          onChange={(next) => onChange({ ...value, answerB: next })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  return typeof aValue === "number" && typeof bValue === "number"
    && Math.abs(aValue - data.tangentA) < 0.0001
    && Math.abs(bValue - data.tangentB) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawA = userAnswer.answerA?.raw;
  const rawB = userAnswer.answerB?.raw;
  if (!rawA && !rawB) return <i>(brak odpowiedzi)</i>;

  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userLineLatex = lineLatexFromUser(aValue, rawA, bValue, rawB);

  return (
    <div className="space-y-2">
      {rawA ? <div><InlineMath math={formatLatexPreview(rawA, "a")} /></div> : <div><InlineMath math={"a = ?"} /></div>}
      {rawB ? <div><InlineMath math={formatLatexPreview(rawB, "b")} /></div> : <div><InlineMath math={"b = ?"} /></div>}
      {userLineLatex ? (
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={userLineLatex} />
        </div>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  const aLatex = simplifyFraction(data.tangentAFraction.numerator, data.tangentAFraction.denominator);
  const bLatex = simplifyFraction(data.tangentBFraction.numerator, data.tangentBFraction.denominator);
  return (
    <div className="space-y-1">
      <div><InlineMath math={`a = ${aLatex}`} /></div>
      <div><InlineMath math={`b = ${bLatex}`} /></div>
      <div><InlineMath math={`y = ${lineLatexExact(aLatex, bLatex, data.tangentA, data.tangentB)}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const numeratorDerivative = `${2 * data.na}x${data.nb >= 0 ? ` + ${data.nb}` : ` - ${Math.abs(data.nb)}`}`;
  const denominatorLatex = `${data.da}x${data.db >= 0 ? ` + ${data.db}` : ` - ${Math.abs(data.db)}`}`;
  const quotientLatex = `\\frac{f'(x)g(x)-f(x)g'(x)}{(g(x))^2}`;
  const y0Latex = simplifyFraction(data.y0Fraction.numerator, data.y0Fraction.denominator);
  const aLatex = simplifyFraction(data.tangentAFraction.numerator, data.tangentAFraction.denominator);
  const bLatex = simplifyFraction(data.tangentBFraction.numerator, data.tangentBFraction.denominator);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Korzystamy ze wzoru na pochodna ilorazu.</p>
      <BlockMath math={`\\left(\\frac{f(x)}{g(x)}\\right)' = ${quotientLatex}`} />
      <p><strong>Krok 2:</strong> Liczymy pochodna licznika i mianownika.</p>
      <BlockMath math={`u(x) = ${formatPoly([data.nc, data.nb, data.na])},\\quad u'(x) = ${numeratorDerivative}`} />
      <BlockMath math={`w(x) = ${denominatorLatex},\\quad w'(x) = ${data.da}`} />
      <p><strong>Krok 3:</strong> Obliczamy wspolczynnik kierunkowy stycznej.</p>
      <BlockMath
        math={`f'(${data.x0}) = \\frac{(${2 * data.na * data.x0 + data.nb})(${data.da * data.x0 + data.db}) - (${data.na * data.x0 * data.x0 + data.nb * data.x0 + data.nc})\\cdot ${data.da}}{(${data.da * data.x0 + data.db})^2} = ${aLatex}`}
      />
      <p><strong>Krok 4:</strong> Liczymy punkt stycznosci.</p>
      <BlockMath math={`f(${data.x0}) = ${y0Latex}`} />
      <BlockMath math={`P = (${data.x0}, ${y0Latex})`} />
      <p><strong>Krok 5:</strong> Podstawiamy do wzoru prostej <InlineMath math={"y = ax + b"} />.</p>
      <BlockMath math={`${y0Latex} = ${aLatex}\\cdot ${data.x0} + b`} />
      <BlockMath math={`b = ${bLatex}`} />
      <p><strong>Krok 6:</strong> Zapisujemy rownanie stycznej.</p>
      <BlockMath math={`y = ${lineLatexExact(aLatex, bLatex, data.tangentA, data.tangentB)}`} />
      <div style={{ maxWidth: 350, margin: "24px auto 0" }}>
        <GraphViewer
          width={350}
          xRange={data.xRange}
          yRange={data.yRange}
          equations={[
            { expr: rationalExpr(data.na, data.nb, data.nc, data.da, data.db), color: "#ff8c1a" },
            { expr: lineExpr(data.tangentA, data.tangentB), color: "#9d4edd" },
          ]}
          points={{
            P: { x: data.x0, y: data.y0, label: "P", color: "#cc5500", textColor: "#cc5500", textPosition: "top center" },
          }}
        />
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
